import api from "./api";
import { ok, listCyclesCached, toCycle, dayStartIso, dayEndIso, DEFAULT_SECTIONS, notImplemented, loadScoringConfig, getMe, can, ORG_SCOPES, listRecords } from "./core";

export async function getCycles(force = true) {
  const [cycles, me] = await Promise.all([listCyclesCached(force), getMe()]);
  const hrView = can(me, "cycle.monitor", ORG_SCOPES) || can(me, "cycle.configure", ORG_SCOPES);
  const visible = hrView ? cycles : cycles.filter((c) => !["DRAFT", "CANCELLED"].includes(c.backendStatus));
  return ok([...visible].sort((a, b) => String(b.startDate).localeCompare(String(a.startDate))));
}

const stage = (form, key) => (form.stages || []).find((s) => s.key === key) || {};

function cycleBody(form) {
  const w = form.workflow || {};
  const window = (key) => ({ start: dayStartIso(stage(form, key).startDate), end: dayEndIso(stage(form, key).endDate) });
  const goal = window("goalSetting");
  const self = window("selfAssessment");
  const manager = window("managerReview");
  const hr = window("hrFinalization");
  return {
    cycle_name: String(form.name || "").trim(),
    performance_start: form.startDate,
    performance_end: form.endDate,
    goal_start_at: goal.start,
    goal_end_at: goal.end,
    self_review_start_at: self.start,
    self_review_end_at: self.end,
    manager_review_start_at: manager.start,
    manager_review_end_at: manager.end,
    hr_finalization_start_at: hr.start,
    hr_finalization_end_at: hr.end,
    scoring_policy_id: Number(form.scoringPolicyId),
    rating_scheme_id: Number(form.ratingSchemeId),
    workflow_config: {
      self_review_required: true,
      manager_review_required: true,
      manager_feedback_visible: w.manager_feedback_visible ?? true,
      release_mode: w.release_mode || "MANUAL",
      acknowledgment_required: w.acknowledgment_required ?? true,
      assessment_sections: (w.assessment_sections && w.assessment_sections.length) ? w.assessment_sections : DEFAULT_SECTIONS,
      hr_owner_employee_id: w.hr_owner_employee_id,
      // [UPDATED] Every cycle must carry workflow_config.progress_approval_required = true (manager approves progress).
      progress_approval_required: true,
    },
    reason: form.reason || "Performance cycle saved",
  };
}

export async function createCycle(form) {
  if (!form.workflow?.hr_owner_employee_id) throw new Error("Choose the HR owner for this cycle.");
  if (!form.scoringPolicyId || !form.ratingSchemeId) throw new Error("Choose a scoring policy and a rating scheme (create them under Scoring & ratings).");
  const { data } = await api.post("/performance-cycles", cycleBody(form));
  return ok(toCycle(data));
}

export async function updateCycle(cycleId, form) {
  const { data } = await api.put(`/performance-cycles/${cycleId}`, cycleBody(form));
  return ok(toCycle(data));
}

export const getCycleReadiness = (cycleId) => api.get(`/performance-cycles/${cycleId}/readiness`);

export const activateCycle = (cycleId, reason = "Cycle launched") =>
  api.post(`/performance-cycles/${cycleId}/launch`, { reason });

export const closeCycle = (cycleId, reason = "Cycle closed") => api.post(`/performance-cycles/${cycleId}/close`, { reason });

export const mapEmployeesToCycle = (cycleId, { employeeIds, applyTemplates = true, reason }) =>
  api.post(`/performance-cycles/${cycleId}/employees`, {
    employee_ids: employeeIds,
    apply_templates: applyTemplates,
    reason: reason || "Employees added to the cycle",
  });

// [NEW] PM-4 (REACT_APP_BACKEND_ACTIVE_CYCLE_DATES=true): change one stage's dates on a running cycle with
// PATCH /performance-cycles/{id}/dates. Appraisals read their windows from the cycle, so everyone gets the new dates.
const STAGE_PREFIX = { GOAL_SETTING: "goal", SELF_ASSESSMENT: "self_review", MANAGER_ASSESSMENT: "manager_review", HR_FINALIZATION: "hr_finalization" };
export async function changeActiveCycleDates(cycleId, { stage, opensOn, closesOn, reason }) {
  const prefix = STAGE_PREFIX[stage];
  const { data } = await api.patch(`/performance-cycles/${cycleId}/dates`, {
    [`${prefix}_start_at`]: dayStartIso(opensOn), [`${prefix}_end_at`]: dayEndIso(closesOn), reason,
  });
  return ok(data || {});
}

export async function updateCycleStage() {
  throw notImplemented("Stages open and close automatically on the dates set for the cycle. To give one person more time, use Deadline override on their review page.");
}

export async function getCycleConfigOptions() {
  const { policies, schemes } = await loadScoringConfig(true);
  return ok({
    policies: (policies || []).map((p) => ({ id: String(p.scoring_policy_id), name: `${p.policy_code} v${p.version_no}`, status: p.policy_status })),
    schemes: (schemes || []).map((s) => ({ id: String(s.rating_scheme_id), name: `${s.scheme_code} v${s.version_no}`, status: s.scheme_status })),
  });
}

// [NEW] Employees already in a cycle (to show and edit them in the "Add employees" picker).
const FINAL_STATES = ["FINALIZED", "RELEASED", "ACKNOWLEDGED", "CLOSED"];
export async function getCycleMembers(cycleId) {
  if (!cycleId) return ok([]);
  const rows = await listRecords(cycleId);
  return ok(rows.map((r) => ({
    employeeId: r.employee_id,
    recordId: r.performance_record_id,
    eligibility: r.eligibility_status,
    recordStatus: r.record_status,
    finalized: FINAL_STATES.includes(r.record_status),
  })));
}

// [NEW] Save the picker: add new people, take people out (eligibility EXCLUDED) and bring excluded people back
// (eligibility ELIGIBLE). The backend has no delete for an appraisal record, so "remove" = exclude; the record,
// its goals and KRAs are kept and come back when the person is ticked again.
export async function updateCycleMembers(cycleId, { add = [], remove = [], restore = [], applyTemplates = true, reason }) {
  const text = reason || "Cycle membership updated";
  const result = { added: 0, alreadyMapped: 0, removed: 0, restored: 0, failed: [] };
  if (add.length) {
    const { data } = await mapEmployeesToCycle(cycleId, { employeeIds: add, applyTemplates, reason: text });
    result.added = data.created?.length || 0;
    result.alreadyMapped = data.already_mapped?.length || 0;
  }
  for (const [list, status, key] of [[remove, "EXCLUDED", "removed"], [restore, "ELIGIBLE", "restored"]]) {
    for (const m of list) {
      try {
        await api.post(`/records/${m.recordId}/eligibility`, { eligibility_status: status, reason: text });
        result[key] += 1;
      } catch (err) {
        result.failed.push({ employeeId: m.employeeId, error: err });
      }
    }
  }
  return ok(result);
}
